import React, { useState, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as Volver } from '../assets/icon/volver.svg';
import { ReactComponent as SubirArchivo } from '../assets/icon/subirarchivo.svg';
import { altaMasivaTarjetas } from '../services/tarjetas';
import { useToast } from '../contexts/ToastContext';
import './ImportarTarjetas.css';

const generateCSVTemplate = () => {
    const headers = ['numeroTarjeta', 'anioVencimiento', 'mesVencimiento', 'tipoTarjeta'];
    const sampleData = [
        ['5412345678901234', '2025', '12', '1'],
        ['5412345678905678', '2026', '06', '1']
    ];
    
    const csvContent = [
        headers.join(','),
        ...sampleData.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'plantilla_tarjetas.csv';
    link.click();
};

const generateXMLTemplate = () => {
    const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<tarjetas>
    <tarjeta>
        <numeroTarjeta>5412345678901234</numeroTarjeta>
        <anioVencimiento>2025</anioVencimiento>
        <mesVencimiento>12</mesVencimiento>
        <tipoTarjeta>1</tipoTarjeta>
    </tarjeta>
    <tarjeta>
        <numeroTarjeta>5412345678905678</numeroTarjeta>
        <anioVencimiento>2026</anioVencimiento>
        <mesVencimiento>06</mesVencimiento>
        <tipoTarjeta>1</tipoTarjeta>
    </tarjeta>
</tarjetas>`;
    
    const blob = new Blob([xmlContent], { type: 'application/xml;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'plantilla_tarjetas.xml';
    link.click();
};

const parseExcelFile = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheet = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheet];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                
                if (jsonData.length < 2) {
                    reject(new Error('El archivo no contiene datos'));
                    return;
                }
                
                const headers = jsonData[0].map(h => h.toString().trim().toLowerCase());
                const requiredHeaders = ['numerotarjeta', 'aniovencimiento', 'mesvencimiento', 'tipotarjeta'];
                
                const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
                if (missingHeaders.length > 0) {
                    reject(new Error(`Faltan columnas requeridas: ${missingHeaders.join(', ')}`));
                    return;
                }
                
                const tarjetas = [];
                for (let i = 1; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    if (row.length === 0 || !row[0]) continue;
                    
                    const getValue = (header) => {
                        const index = headers.indexOf(header);
                        return index >= 0 && row[index] ? row[index].toString().trim() : '';
                    };
                    
                    const numeroTarjeta = getValue('numerotarjeta');
                    const anioVencimiento = getValue('aniovencimiento');
                    const mesVencimiento = getValue('mesvencimiento');
                    const tipoTarjeta = getValue('tipotarjeta');
                    
                    if (!numeroTarjeta) continue;
                    
                    tarjetas.push({
                        numeroTarjeta,
                        anioVencimiento,
                        mesVencimiento,
                        tipoTarjeta
                    });
                }
                
                if (tarjetas.length === 0) {
                    reject(new Error('No se encontraron tarjetas válidas en el archivo'));
                    return;
                }
                
                resolve(tarjetas);
            } catch (error) {
                reject(new Error('Error al procesar el archivo Excel: ' + error.message));
            }
        };
        
        reader.onerror = () => reject(new Error('Error al leer el archivo'));
        reader.readAsArrayBuffer(file);
    });
};

const parseCSVFile = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const text = e.target.result;
                const lines = text.split('\n').filter(line => line.trim());
                
                if (lines.length < 2) {
                    reject(new Error('El archivo no contiene datos'));
                    return;
                }
                
                const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
                const requiredHeaders = ['numerotarjeta', 'aniovencimiento', 'mesvencimiento', 'tipotarjeta'];
                
                const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
                if (missingHeaders.length > 0) {
                    reject(new Error(`Faltan columnas requeridas: ${missingHeaders.join(', ')}`));
                    return;
                }
                
                const tarjetas = [];
                for (let i = 1; i < lines.length; i++) {
                    const values = lines[i].split(',').map(v => v.trim());
                    
                    if (values.length === 0 || !values[0]) continue;
                    
                    const getValue = (header) => {
                        const index = headers.indexOf(header);
                        return index >= 0 && values[index] ? values[index] : '';
                    };
                    
                    const numeroTarjeta = getValue('numerotarjeta');
                    const anioVencimiento = getValue('aniovencimiento');
                    const mesVencimiento = getValue('mesvencimiento');
                    const tipoTarjeta = getValue('tipotarjeta');
                    
                    if (!numeroTarjeta) continue;
                    
                    tarjetas.push({
                        numeroTarjeta,
                        anioVencimiento,
                        mesVencimiento,
                        tipoTarjeta
                    });
                }
                
                if (tarjetas.length === 0) {
                    reject(new Error('No se encontraron tarjetas válidas en el archivo'));
                    return;
                }
                
                resolve(tarjetas);
            } catch (error) {
                reject(new Error('Error al procesar el archivo CSV: ' + error.message));
            }
        };
        
        reader.onerror = () => reject(new Error('Error al leer el archivo'));
        reader.readAsText(file);
    });
};

function ImportarTarjetas() {
    const navigate = useNavigate();
    const { showSuccess, showError, showWarning } = useToast();
    const fileInputRef = useRef(null);
    
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewData, setPreviewData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    
    const handleFileSelect = useCallback(async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        setIsLoading(true);
        setSelectedFile(file);
        setPreviewData([]);
        
        try {
            let tarjetas;
            const fileType = file.name.split('.').pop().toLowerCase();
            
            if (fileType === 'xlsx' || fileType === 'xls') {
                tarjetas = await parseExcelFile(file);
            } else if (fileType === 'csv') {
                tarjetas = await parseCSVFile(file);
            } else {
                throw new Error('Tipo de archivo no soportado. Use Excel (.xlsx, .xls) o CSV');
            }
            
            setPreviewData(tarjetas.slice(0, 10));
            showSuccess(`Archivo cargado correctamente. ${tarjetas.length} tarjetas encontradas.`);
        } catch (error) {
            showError(error.message);
            setSelectedFile(null);
        } finally {
            setIsLoading(false);
        }
    }, [showSuccess, showError]);
    
    const handleDrop = useCallback(async (event) => {
        event.preventDefault();
        const file = event.dataTransfer.files?.[0];
        if (!file) return;
        
        const dataTransfer = { target: { files: [file] } };
        handleFileSelect(dataTransfer);
    }, [handleFileSelect]);
    
    const handleDragOver = useCallback((event) => {
        event.preventDefault();
    }, []);
    
    const handleImport = useCallback(async () => {
        if (previewData.length === 0) return;
        
        setIsUploading(true);
        
        try {
            const result = await altaMasivaTarjetas(previewData);
            
            if (result.success && result.error === 0) {
                const data = result.data || {};
                const exitosos = data.exitosos || 0;
                const fallidos = data.fallidos || 0;
                
                showSuccess(`${result.mensaje || `Importación completada: ${exitosos} exitosas, ${fallidos} fallidas`}`);
                
                setTimeout(() => {
                    navigate('/dashboard/Tarjetas');
                }, 2000);
            } else {
                showError(result.mensaje || 'Error al importar tarjetas');
            }
        } catch (error) {
            showError(error.message || 'Error al conectar con el servidor');
        } finally {
            setIsUploading(false);
        }
    }, [previewData, navigate, showSuccess, showError]);
    
    const handleCancel = useCallback(() => {
        setSelectedFile(null);
        setPreviewData([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);
    
    return (
        <div className="bodyImportarTarjetas">
            <div className="tituloImportarTarjetas">
                <p>Importar Tarjetas</p>
            </div>
            
            <div className="cuerpoBotonVolverTarjetas">
                <button
                    className="botonesAgregarTarjetas"
                    onClick={() => navigate("/dashboard/Tarjetas")}
                    type="button"
                >
                    <div className="svgBotones">
                        <Volver className="logo-svgBotones" />
                    </div>
                    Volver
                </button>
            </div>
            
            <div className="cuerpoImportarTarjetas">
                <div className="plantillasSection">
                    <h3>Descargar Plantillas</h3>
                    <p>Descargue una plantilla para saber el formato correcto de los datos</p>
                    <div className="botonesPlantilla">
                        <button 
                            className="btn-plantilla csv"
                            onClick={generateCSVTemplate}
                            type="button"
                        >
                            Descargar CSV
                        </button>
                        <button 
                            className="btn-plantilla xml"
                            onClick={generateXMLTemplate}
                            type="button"
                        >
                            Descargar XML
                        </button>
                    </div>
                </div>
                
                <div className="uploadSection">
                    <h3>Subir Archivo</h3>
                    <p>Archivos permitidos: .xlsx, .xls, .csv</p>
                    
                    <div
                        className={`uploadBox ${selectedFile ? 'has-file' : ''} ${isLoading ? 'loading' : ''}`}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onClick={() => !isLoading && fileInputRef.current?.click()}
                    >
                        <input
                            type="file"
                            ref={fileInputRef}
                            accept=".xlsx,.xls,.csv"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                        
                        {isLoading ? (
                            <div className="loading-indicator">
                                <div className="spinner"></div>
                                <p>Procesando archivo...</p>
                            </div>
                        ) : selectedFile ? (
                            <>
                                <div className="file-info">
                                    <SubirArchivo className="file-icon" />
                                    <p className="file-name">{selectedFile.name}</p>
                                    <p className="file-size">{(selectedFile.size / 1024).toFixed(2)} KB</p>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="iconSubirArchivo">
                                    <div className="svgSubirArchivo">
                                        <SubirArchivo className="logosvgSubirArchivo" />
                                    </div>
                                </div>
                                <p>Seleccione o arrastre aquí los archivos o</p>
                                <label className="link">
                                    haga click para seleccionar
                                </label>
                            </>
                        )}
                    </div>
                    
                    {previewData.length > 0 && (
                        <div className="previewSection">
                            <h4>Vista Previa (primeros 10 registros)</h4>
                            <div className="previewTable">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Número Tarjeta</th>
                                            <th>Año Vencimiento</th>
                                            <th>Mes Vencimiento</th>
                                            <th>Tipo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewData.map((tarjeta, index) => (
                                            <tr key={index}>
                                                <td>{tarjeta.numeroTarjeta}</td>
                                                <td>{tarjeta.anioVencimiento}</td>
                                                <td>{tarjeta.mesVencimiento}</td>
                                                <td>{tarjeta.tipoTarjeta}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <p className="total-registers">Total de tarjetas a importar: {previewData.length}</p>
                        </div>
                    )}
                    
                    <div className="accionesSection">
                        <button 
                            className="btn-cancel"
                            onClick={handleCancel}
                            disabled={!selectedFile || isUploading}
                            type="button"
                        >
                            Cancelar
                        </button>
                        <button 
                            className="btn-import"
                            onClick={handleImport}
                            disabled={previewData.length === 0 || isUploading}
                            type="button"
                        >
                            {isUploading ? 'Importando...' : `Importar ${previewData.length} Tarjetas`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ImportarTarjetas;
